// Clone a program: name + start date → POST /programs/{id}/clone. The server
// shifts every session so the first one starts on that date; the copy is a
// draft with no participants.
import { useT } from '../lib/i18n';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import type { Program } from '@/services/learningService';
import { useCloneProgram } from '../api/hooks';
import { localDayOf } from '../lib/format';

interface Props { program: Program | null; onClose: () => void; onCloned: (program: Program) => void }

export const CloneProgramDialog: React.FC<Props> = ({ program, onClose, onCloned }) => {
  const t = useT();
  const clone = useCloneProgram();
  return (
    <Dialog open={!!program} onOpenChange={(o) => { if (!o && !clone.isPending) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('training:clone_title', 'Clone program')}</DialogTitle>
          <DialogDescription>{t('training:clone_help', 'Creates a draft copy with every session shifted so the first one starts on the chosen date. Audience rules and tracks are copied; participants are not.')}</DialogDescription>
        </DialogHeader>
        {program && (
          <CloneForm key={program.id} program={program} busy={clone.isPending} onClose={onClose}
            onConfirm={(body) => clone.mutate({ id: program.id, body }, { onSuccess: (p) => { toast.success(t('training:clone_ok', 'Program cloned as a draft')); onCloned(p); } })} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const CloneForm: React.FC<{ program: Program; busy: boolean; onClose: () => void; onConfirm: (b: { name?: string; starts_on: string }) => void }> = ({ program, busy, onClose, onConfirm }) => {
  const t = useT();
  const [name, setName] = useState(t('training:clone_default_name', '{{name}} (copy)', { name: program.name }));
  const [startsOn, setStartsOn] = useState(localDayOf(new Date().toISOString()));
  return (
    <div className="space-y-3" data-testid="clone-dialog">
      <div className="space-y-1">
        <Label htmlFor="clone-name" className="text-xs">{t('training:wiz_name', 'Program name')}</Label>
        <Input id="clone-name" className="h-9" value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="clone-start" className="text-xs">{t('training:clone_starts_on', 'First session on')}</Label>
        <Input id="clone-start" className="h-9" type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} disabled={busy} />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>{t('training:cancel', 'Cancel')}</Button>
        <Button size="sm" disabled={!startsOn || busy} data-testid="clone-confirm" onClick={() => onConfirm({ name: name.trim() || undefined, starts_on: startsOn })}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:clone_confirm', 'Clone')}
        </Button>
      </div>
    </div>
  );
};

export default CloneProgramDialog;
